import React from 'react';

import { Link } from 'react-router-dom';

import {
  AppstoreOutlined,
  BuildOutlined,
  DeploymentUnitOutlined,
  EnvironmentOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { IntegratedErps, TenantFeatureFlag, TenantProductName } from '@recurrency/core-api-schema/dist/common/enums';
import { schema } from '@recurrency/core-api-schema/dist/companies';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';
import { ColumnType } from 'antd/lib/table';

import { ButtonLink } from 'components/Links';
import { NewItemButton } from 'components/recipes/NewItemButton';
import { SearchFrame } from 'components/recipes/SearchFrame';
import { ValueFacet } from 'components/recipes/SearchFrame/types';

import { NULLIFY_API_CALL, useCoreApi, useLegacyApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { truthy } from 'utils/boolean';
import { formatNumber, pluralize } from 'utils/formatting';
import { shouldShowFeatureFlag, shouldShowProduct } from 'utils/roleAndTenant';
import { useHashState } from 'utils/routes';
import { sortableNumberColumn, sortableStringColumn } from 'utils/tables';
import { isMaxShelfLifeEnabled } from 'utils/tenantSettings';
import { qtyUnitConverter } from 'utils/units';

import { SearchFrameHashState } from 'types/hash-state';
import { SalesRepLocationResponse } from 'types/legacy-api';
import { SearchIndexItem } from 'types/search-collections';

import { getTenantConfiguration } from '../../utils/tenantConf/tenantConf';
import { productTypeFacetOptions } from '../purchasing/utils';

export const ItemListPage = ({ detailsPageLinkFn }: { detailsPageLinkFn: (id: string) => string }) => {
  const [hashState] = useHashState<SearchFrameHashState>();
  const { activeTenant, activeUser, activeCompanyIds, userAllowedCompanies } = useGlobalApp();
  const maxShelfLifeEnabled = isMaxShelfLifeEnabled();

  const hasSalesQuotingProduct = shouldShowProduct(activeTenant, TenantProductName.SalesQuoting);
  const allCompanyData = useCoreApi(
    schema.getCompanies,
    hasSalesQuotingProduct ? { queryParams: { allCompanies: true } } : null,
  );

  // only show qty available if there's one or no companies
  // this is because the qty is an aggregation of all companies and users may only be able to view a subset of these
  const showCompanyQty = allCompanyData.data ? allCompanyData.data.items.length < 2 : false;

  // stock location comparison is only for legacy quoting tenants like cwc/galarson
  // show stock for salesrep's default location
  // locationId -1 implies user is admin and has no default location
  const { data: salesRepLocationData } = useLegacyApi<SalesRepLocationResponse>(
    hasSalesQuotingProduct ? '/v3/salesrep' : NULLIFY_API_CALL,
  );
  const salesRepLocation =
    salesRepLocationData && salesRepLocationData.locationId !== -1
      ? `${salesRepLocationData.locationId}: ${salesRepLocationData.locationName}`
      : '';

  let stockLocationsToCompare: string[] = [];
  if (hasSalesQuotingProduct) {
    if (hashState.where?.in_stock_locations?.length) {
      stockLocationsToCompare = hashState.where.in_stock_locations;
    }
    if (salesRepLocation && !stockLocationsToCompare.includes(salesRepLocation)) {
      stockLocationsToCompare.unshift(salesRepLocation);
    }
  }

  const columns: (ColumnType<SearchIndexItem> | null)[] = [
    {
      title: 'ID',
      dataIndex: 'item_id',
      render: (id: string, item: SearchIndexItem) => (
        <Link to={detailsPageLinkFn(id)}>
          {id} {item.is_assembly ? <DeploymentUnitOutlined /> : null}
        </Link>
      ),
    },
    sortableStringColumn({
      title: 'Item',
      dataIndex: 'item_desc',
      sorter: true,
    }),
    {
      title: 'Description',
      dataIndex: 'extended_desc',
    },
    ...stockLocationsToCompare.map((locationIdNameStr) => ({
      title: `${locationIdNameStr} Stock`,
      dataIndex: 'stock_by_location',
      render: (stockByLocation: Obj<number>, item: SearchIndexItem) =>
        formatNumber(qtyUnitConverter(stockByLocation[locationIdNameStr], item.unit_size)),
      align: 'right' as const,
    })),
    showCompanyQty
      ? sortableNumberColumn({
          title: 'Company Qty Available',
          dataIndex: 'stock',
          render: (companyStock: number, item: SearchIndexItem) =>
            formatNumber(qtyUnitConverter(companyStock, item.unit_size)),
          sorter: true,
          align: 'right' as const,
          defaultSortOrder: 'descend',
        })
      : null,
    maxShelfLifeEnabled
      ? sortableNumberColumn({
          title: 'Max Shelf Life (days)',
          dataIndex: 'max_shelf_life_days',
          render: (days: number | null | undefined) => (days != null ? pluralize(days, 'day', 'days', true) : '—'),
          sorter: true,
          align: 'right' as const,
          optional: true,
        })
      : null,
    {
      title: 'UOM',
      dataIndex: 'uom',
    },
    {
      title: '',
      dataIndex: 'item_id',
      render: (itemId: string) => ButtonLink(detailsPageLinkFn(itemId)),
    },
  ];

  const valueFacets: (ValueFacet<SearchIndexItem> | null)[] = [
    activeTenant.erpType === IntegratedErps.P21
      ? {
          title: 'Type',
          field: 'is_assembly',
          labelFormatFn: (value: string | boolean) =>
            value === 'true' || value === true ? 'Assembly' : 'Non-Assembly',
          icon: <BuildOutlined />,
        }
      : null,
    activeTenant.erpType === IntegratedErps.P21 &&
    shouldShowFeatureFlag(activeTenant, activeUser, TenantFeatureFlag.FeatureProductTypeSearchFilter)
      ? {
          title: 'Product Type',
          field: 'product_type',
          icon: <BuildOutlined />,
          options: [...productTypeFacetOptions],
          sortBy: 'none' as const,
        }
      : null,
    getTenantConfiguration(activeTenant).itemsListPage.hideProductGroupFilter
      ? null
      : {
          title: 'Product Group',
          field: 'pg',
          queryPlaceholder: 'Search product groups',
          icon: <AppstoreOutlined />,
        },
    {
      title: 'In-Stock Location',
      field: 'in_stock_locations',
      queryPlaceholder: 'Search locations',
      icon: <EnvironmentOutlined />,
      // Set static list of options to only those locations that user is allowed to access.
      // Since 'Out of Stock' is a virtual location shared between companies and locations,
      // filtering in_stock_locations doesn't work.
      options:
        userAllowedCompanies.length > 0
          ? [
              ...userAllowedCompanies
                .filter((c) => (activeCompanyIds ? activeCompanyIds.includes(c.companyId) : true))
                .flatMap((c) => c.locations.map((l) => `${l.locationId}: ${l.locationName}`)),
              'Out of stock',
            ].map((locationIdNameStr) => ({ value: locationIdNameStr, label: locationIdNameStr }))
          : undefined,
    },
    activeTenant.erpType === IntegratedErps.P21
      ? {
          title: 'Supplier',
          field: 'suppliers',
          queryPlaceholder: 'Search suppliers',
          icon: <ShopOutlined />,
        }
      : null,
  ];

  return (
    <SearchFrame<SearchIndexItem>
      title="Inventory"
      indexName={SearchIndexName.Items}
      queryPlaceholder="Search items by ID, description, product group, or keywords"
      columns={columns.filter(truthy)}
      valueFacets={valueFacets.filter(truthy)}
      headerActions={
        <>
          {shouldShowFeatureFlag(activeTenant, activeUser, TenantFeatureFlag.SalesCreateItem) && (
            <NewItemButton type="primary" />
          )}
          {shouldShowFeatureFlag(activeTenant, activeUser, TenantFeatureFlag.SalesCreateAssemblyItem) && (
            <NewItemButton type="primary" assemblyItem />
          )}
        </>
      }
      tableRowKey="item_id"
    />
  );
};
